import React, { useEffect, useRef, useState } from 'react';
import './biz-mediabank.scss';
import { BizCheckBox } from '../Mini/BizCheckBox';
import { SelectedFileCallback } from './BizMediabankContext';
import { MediabankFileType, MediabankSettings, MediabankUploadLocation } from '../../Types/mediabank';
import { BizButton } from '../Mini/BizButton';
import { useMediabankMutations } from '../../Hooks/useMediabank';
import MediaFolderSelect from './MediaFolderSelect';
import { useTranslation } from 'react-i18next';

type MediabankDialogUploadProps = {
	onClose: SelectedFileCallback;
	fileTypes: MediabankFileType[];
	uploadLocation: MediabankUploadLocation;
	settings?: MediabankSettings;
	legacy?: boolean;
};

const BizMediabankDialogUpload = ({
	onClose,
	fileTypes: fileType,
	uploadLocation,
	settings,
	legacy,
}: MediabankDialogUploadProps) => {
	const { t } = useTranslation();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [file, setFile] = useState<File>();
	const [isPrivate, setIsPrivate] = useState(false);
	const [isInvisible, setIsInvisible] = useState(false);
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [folderId, setFolderId] = useState<number | null>(settings?.folderId ?? null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const { uploadFile, uploadFileResult, statusStateUploadFile, uploadFileError } = useMediabankMutations();

	useEffect(() => {
		if (uploadFileResult && statusStateUploadFile === 'SUCCESS') {
			onClose(uploadFileResult);
		}
	}, [uploadFileResult, statusStateUploadFile]);

	useEffect(() => {
		if (uploadFileError && statusStateUploadFile === 'ERROR') {
			setErrorMessage(`${uploadFileError}`);
		}

	}, [uploadFileError, statusStateUploadFile]);

	useEffect(() => {
		if (settings?.isPrivate !== undefined) {
			setIsPrivate(settings?.isPrivate);
		}
		if (settings?.isInvisible !== undefined) {
			setIsInvisible(settings?.isInvisible);
		}
	}, []);

	const uploadClickHandler = () => {
		if (file) {
			uploadFile(file, name, description, isPrivate, isInvisible, uploadLocation, { ...settings, folderId });
		} else {
			throw new Error(t('MEDIABANK.UPLOAD_DIALOG.FILE_NOT_SET'));
		}
	};

	const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
	};

	const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		handleFiles(e.dataTransfer.files);
	};

	const handleFiles = (files: FileList) => {
		if (files.length) {
			setFile(files[0]);
			setName(files[0].name);
		}
	};

	const handleClick = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (files) {
			handleFiles(files);
		}
	};

	const getFileTypePattern = () => {
		if (fileType.length === 1) {
			return fileType[0].pattern;
		} else {
			return fileType
				.filter((ft) => ft.pattern)
				.map((ft) => ft.pattern)
				.join(',');
		}
	};

	return (
		<div className='bizmediabank-react-dialog-upload'>
			<MediaFolderSelect value={folderId} onChange={setFolderId} disabled={statusStateUploadFile === 'LOADING'} />
			<input
				ref={fileInputRef}
				type='file'
				onChange={handleInputChange}
				style={{ display: 'none' }}
				accept={getFileTypePattern()}
			/>
			<div className='dropzone' onClick={handleClick} onDragOver={onDragOver} onDrop={onDrop}>
				<div className='dropzone-content'>
					<div className='icon'>
						<i className='far fa-file'></i>
					</div>
					<div className='text'>{t('MEDIABANK.UPLOAD_DIALOG.DROPZONE')}</div>
				</div>
			</div>
			{file !== undefined && (
				<>
					<div className='file-info'>
						<div>{t('MEDIABANK.UPLOAD_DIALOG.FILE', { name: file.name })}</div>
						<div>{t('MEDIABANK.UPLOAD_DIALOG.SIZE', { size: Math.round(file.size / 1024) })}</div>
					</div>
					<div className='margin-top'>
						<span className='control-label'>{t('MEDIABANK.UPLOAD_DIALOG.FILE_NAME')}</span>
						<input className='form-control' value={name} onChange={(e) => setName(e.target.value)} />
					</div>
					<div className='margin-top'>
						<span className='control-label'>{t('MEDIABANK.UPLOAD_DIALOG.FILE_DESCRIPTION')}</span>
						<textarea className='form-control' value={description} onChange={(e) => setDescription(e.target.value)} />
					</div>
				</>
			)}

			{settings?.isPrivate === undefined && settings?.isInvisible === undefined && (
				<div className='margin-top-20'>
					{settings?.isPrivate === undefined && (
						<BizCheckBox 
							value={isPrivate} 
							label={t('MEDIABANK.UPLOAD_DIALOG.AS_PRIVATE')}
							onChange={(checked) => setIsPrivate(checked)} 
						/>
					)}
					{settings?.isInvisible === undefined && (
						<BizCheckBox
							value={isInvisible}
							label={t('MEDIABANK.UPLOAD_DIALOG.AS_INVISIBLE')}
							onChange={(checked) => setIsInvisible(checked)}
						/>
					)}
				</div>
			)}

			{errorMessage && <div className='margin-top-20 error-message'>{errorMessage}</div>}
			
			{file !== undefined && (
				<div className='margin-top-20'>
					<BizButton
						state={statusStateUploadFile}
						onClick={uploadClickHandler}
						text={t('MEDIABANK.UPLOAD_DIALOG.ACTION')}
						icon='far fa-upload'
						legacy={legacy}
					/>
				</div>
			)}
		</div>
	);
};

export default BizMediabankDialogUpload;
